import { TABLE_RESIZE_HANDLE_THRESHOLD } from "@corca/tiptap";
import { TableMap } from "@tiptap/pm/tables";

import type { ColumnEdge, RowEdge } from "@/components/tiptap/tables/resize/types";
import type { Node } from "@tiptap/pm/model";

export function findNearestColumnEdge(container: HTMLElement, clientX: number): ColumnEdge | null {
  const table = container.querySelector("table");

  if (!table) {
    return null;
  }

  const firstRow = table.querySelector("tr");

  if (!firstRow) {
    return null;
  }

  const cells = Array.from(firstRow.children) as HTMLTableCellElement[];

  // Exclude the last column
  cells.pop();

  let colIdx = 0;

  for (const cell of cells) {
    const colspan = cell.colSpan || 1;

    if (colspan === 1) {
      const cellRect = cell.getBoundingClientRect();

      if (Math.abs(clientX - cellRect.right) <= TABLE_RESIZE_HANDLE_THRESHOLD) {
        const containerRect = container.getBoundingClientRect();

        return {
          colIndex: colIdx,
          rightEdgeX: cellRect.right - containerRect.left,
          absoluteRightEdgeX: cellRect.right,
          width: cellRect.width,
        };
      }
    }

    colIdx += colspan;
  }

  return null;
}

export function findNearestRowEdge(container: HTMLElement, clientY: number): RowEdge | null {
  const table = container.querySelector("table");

  if (!table) {
    return null;
  }

  const rows = Array.from(table.querySelectorAll("tr"));

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const rowRect = rows[rowIndex].getBoundingClientRect();

    if (Math.abs(clientY - rowRect.bottom) <= TABLE_RESIZE_HANDLE_THRESHOLD) {
      const containerRect = container.getBoundingClientRect();

      return {
        rowIndex,
        bottomEdgeY: rowRect.bottom - containerRect.top,
        absoluteBottomEdgeY: rowRect.bottom,
        height: rowRect.height,
      };
    }
  }

  return null;
}

export function getTableProps(node: Node, tablePos: number) {
  const tableStart = tablePos + 1;
  const map = TableMap.get(node);

  return { map, tableStart };
}

export function getCellPos(node: Node, tablePos: number, colIndex: number) {
  const { map, tableStart } = getTableProps(node, tablePos);
  return tableStart + map.map[colIndex];
}

export function getRowPos(node: Node, tablePos: number, rowIndex: number) {
  const { map, tableStart } = getTableProps(node, tablePos);
  const firstCellOffset = map.map[rowIndex * map.width];

  return tableStart + firstCellOffset - 1;
}
